import React, { useState } from 'react';
import Loader from '../../lib/loader/Loader';
import './circular-image-upload.css';
import { debounce } from '../../utils';
import ImageInputPlaceholder from '../../assets/image-input-placeholder.png';

interface ICircularImageUploadProps {
	src?: string;
	onChange: (files: File[], lastPicture: string) => void;
	hoverLabel?: string;
	dragLabel?: string;
}

const CircularImageUpload: React.FC<ICircularImageUploadProps> = ({
	src,
	onChange,
	hoverLabel,
	dragLabel,
}) => {
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [lastPicture, setLastPicture] = useState<string>('');
	const [picture, setPicture] = useState<string>(src || '');

	const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		event.preventDefault();
		const file = event.target.files?.[0];
		if (!file) return;

		const url = URL.createObjectURL(file);
		setLastPicture(picture);
		setPicture(url);
		setIsSubmitting(true);

		await onChange(event.target.files as any, lastPicture);

		setIsSubmitting(false);
	};

	const handleFileDrop = async (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		const files = event.dataTransfer.files;
		if (!files.length) return;

		const file = files[0];
		setIsSubmitting(true);

		await onChange([file], lastPicture);

		setIsSubmitting(false);
		setLastPicture(picture);
		setPicture(URL.createObjectURL(file));

		const dragOverLabel = document.querySelector('.profile-pic_drag-label');
		dragOverLabel?.classList.remove('profile-pic_dragover-active');
	};

	const handleDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
		event.preventDefault();
		event.currentTarget?.classList.add('profile-pic_dragover-active');
	};

	const handleDragLeave = debounce(
		(event: React.DragEvent<HTMLLabelElement>) => {
			event.preventDefault();
			event.currentTarget?.classList.remove('profile-pic_dragover-active');
		},
		1000
	);

	return (
		<div className='circular-profile-pic-container'>
			<div className='circular-profile-pic' onDrop={handleFileDrop}>
				{!isSubmitting && (
					<>
						<label className='profile-pic_hover-label' htmlFor='file'>
							<span>{hoverLabel || 'Change Image'}</span>
						</label>
						<label
							className='profile-pic_drag-label'
							htmlFor='file'
							onDragOver={handleDragOver}
							onDragLeave={handleDragLeave}
						>
							<span className='glyphicon glyphicon-camera'></span>
							<span>{dragLabel || 'Drop Image'}</span>
						</label>
					</>
				)}
				{isSubmitting && (
					<div className='profile-pic_loader-container'>
						<Loader size={24} />
					</div>
				)}
				<input
					id='file'
					type='file'
					accept='image/*'
					onChange={handleChange}
					disabled={isSubmitting}
				/>
				<img
				// Fallback to avatar picture if src is not provided
					src={picture || ImageInputPlaceholder}
					id='output'
					width='200'
					alt='Profile'
				/>
			</div>
		</div>
	);
};

export default CircularImageUpload;
